import { createBrowserRouter, redirect } from 'react-router';
import { GlobalLoadingFallback } from './components/GlobalLoadingFallback';
import { PageErrorBoundary } from './components/PageErrorBoundary';
import { RequirePermission } from './components/RequirePermission';
import { STOCK_MODULE_ACTION } from './lib/app-modules';
import { HR_CHILD_MODULES, MODULE_KEYS } from './lib/module-keys';
import { getAuthState, type AuthState } from './lib/auth';
import { lazyWithRetry } from './lib/lazyWithRetry';
import { EDF_DEBTOR_AGEING_ENABLED } from './lib/reports/edf-debtor-ageing-disabled';
import { RECEIVABLES_AGEING_ENABLED } from './lib/reports/receivables-ageing-disabled';
import Root, { RootErrorBoundary } from './root';

function RouteLoadingFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <GlobalLoadingFallback />
    </div>
  );
}

/**
 * Where a signed-in person lands. Loaders run before permissions load, so the
 * choice of module is left to the start route, which can check them.
 */
const START_PATH = '/start';

/**
 * Paths from before the module became the first segment. Bookmarks, emailed
 * links and notifications still carry them, so each forwards to its place
 * under `/crm` with the query string intact.
 */
const LEGACY_CRM_SEGMENTS = [
  'dashboard',
  'leads',
  'customers',
  'contracts',
  'commissions',
  'banking',
  'bank-verification',
  'agent-agreements',
  'transactions',
  'stock',
  'tasks',
  'workshop',
  'approvals',
];

function legacyCrmRedirect({ request }: { request: Request }): Response {
  const url = new URL(request.url);
  return redirect(`/crm${url.pathname}${url.search}${url.hash}`);
}

const legacyCrmRoutes = LEGACY_CRM_SEGMENTS.flatMap(segment => [
  { path: segment, loader: legacyCrmRedirect },
  { path: `${segment}/*`, loader: legacyCrmRedirect },
]);

function publicLoader(): AuthState | Response {
  const auth = getAuthState();

  if (auth.isAuthenticated) {
    if (auth.user && !auth.user.confirmed) {
      return redirect('/verify-email');
    }
    return redirect(START_PATH);
  }

  return auth;
}

function protectedLoader({ request }: { request: Request }): AuthState | Response {
  const auth = getAuthState();

  if (!auth.isAuthenticated) {
    const url = new URL(request.url);
    const searchParams = new URLSearchParams();
    const fromPath = url.search ? `${url.pathname}${url.search}` : url.pathname;
    searchParams.set('from', fromPath);
    return redirect(`/login?${searchParams.toString()}`);
  }

  if (auth.user && !auth.user.confirmed) {
    return redirect('/verify-email');
  }

  return auth;
}

function verifyEmailLoader(): AuthState | Response {
  const auth = getAuthState();

  if (!auth.isAuthenticated) {
    return redirect('/login');
  }

  if (auth.user?.confirmed) {
    return redirect(START_PATH);
  }

  return auth;
}

export const router = createBrowserRouter([
  {
    path: '/',
    Component: Root,
    ErrorBoundary: RootErrorBoundary,
    HydrateFallback: RouteLoadingFallback,
    children: [
      // Redirect root to dashboard or login
      {
        index: true,
        loader: () => {
          const auth = getAuthState();
          if (auth.isAuthenticated) {
            return redirect(START_PATH);
          }
          return redirect('/login');
        },
      },

      ...legacyCrmRoutes,

      // Public routes
      {
        path: 'login',
        loader: publicLoader,
        lazy: lazyWithRetry(async () => {
          const { default: Component } = await import('./pages/Login');
          return { Component, ErrorBoundary: PageErrorBoundary };
        }),
      },
      {
        path: 'accept-invite',
        lazy: lazyWithRetry(async () => {
          const { default: Component } = await import('./pages/AcceptInvite');
          return { Component, ErrorBoundary: PageErrorBoundary };
        }),
      },

      // Email verification
      {
        path: 'verify-email',
        loader: verifyEmailLoader,
        lazy: lazyWithRetry(async () => {
          const { default: Component } = await import('./pages/VerifyEmail');
          return { Component, ErrorBoundary: PageErrorBoundary };
        }),
      },

      // Password reset
      {
        path: 'forgot-password',
        lazy: lazyWithRetry(async () => {
          const { default: Component } = await import('./pages/ForgotPassword');
          return { Component, ErrorBoundary: PageErrorBoundary };
        }),
      },
      {
        path: 'reset-password',
        lazy: lazyWithRetry(async () => {
          const { default: Component } = await import('./pages/ResetPassword');
          return { Component, ErrorBoundary: PageErrorBoundary };
        }),
      },

      // Logout
      {
        path: 'logout',
        lazy: lazyWithRetry(async () => {
          const { default: Component, action } = await import('./pages/Logout');
          return { Component, action, ErrorBoundary: PageErrorBoundary };
        }),
      },

      // Protected routes with AppLayout
      {
        loader: protectedLoader,
        lazy: lazyWithRetry(async () => {
          const { default: Component } = await import('./layouts/AppLayout');
          return { Component, ErrorBoundary: PageErrorBoundary };
        }),
        children: [
          {
            path: 'start',
            lazy: lazyWithRetry(async () => {
              const { default: Component } = await import('./pages/StartRedirect');
              return { Component, ErrorBoundary: PageErrorBoundary };
            }),
          },
          // The CRM module. The module is the first path segment, so the sidebar's
          // module switcher reads the active module straight off the URL.
          {
            path: 'crm',
            children: [
              { index: true, loader: () => redirect('/crm/dashboard') },
              {
                path: 'dashboard',
                lazy: lazyWithRetry(async () => {
                  const { default: Component } = await import('./pages/Dashboard');
                  return { Component, ErrorBoundary: PageErrorBoundary };
                }),
              },

              // Module pages
              {
                path: 'leads',
                children: [
                  {
                    index: true,
                    lazy: lazyWithRetry(async () => {
                      const { default: Component } = await import('./pages/leads/index');
                      return { Component, ErrorBoundary: PageErrorBoundary };
                    }),
                  },
                  {
                    path: ':id',
                    lazy: lazyWithRetry(async () => {
                      const { default: Component } = await import('./pages/leads/[id]');
                      return { Component, ErrorBoundary: PageErrorBoundary };
                    }),
                  },
                ],
              },
              {
                path: 'customers',
                children: [
                  {
                    index: true,
                    lazy: lazyWithRetry(async () => {
                      const { default: Component } = await import('./pages/customers/index');
                      return { Component, ErrorBoundary: PageErrorBoundary };
                    }),
                  },
                  {
                    path: 'new',
                    lazy: lazyWithRetry(async () => {
                      const { default: Component } = await import('./pages/customers/new');
                      return { Component, ErrorBoundary: PageErrorBoundary };
                    }),
                  },
                  {
                    // Before ':id' so the literal segment is not read as a customer id.
                    path: 'mine',
                    lazy: lazyWithRetry(async () => {
                      const { default: Component } = await import('./pages/customers/mine');
                      return { Component, ErrorBoundary: PageErrorBoundary };
                    }),
                  },
                  {
                    path: ':id',
                    lazy: lazyWithRetry(async () => {
                      const { default: Component } = await import('./pages/customers/[id]');
                      return { Component, ErrorBoundary: PageErrorBoundary };
                    }),
                  },
                  {
                    path: ':customerId/applications/new',
                    lazy: lazyWithRetry(async () => {
                      const { default: Component } = await import('./pages/customers/[customerId]/applications/new');
                      return { Component, ErrorBoundary: PageErrorBoundary };
                    }),
                  },
                  {
                    path: ':customerId/applications/:applicationId',
                    lazy: lazyWithRetry(async () => {
                      const { default: Component } = await import('./pages/customers/[customerId]/applications/[applicationId]');
                      return { Component, ErrorBoundary: PageErrorBoundary };
                    }),
                  },
                ],
              },
              {
                path: 'contracts',
                children: [
                  {
                    index: true,
                    lazy: lazyWithRetry(async () => {
                      const { default: Component } = await import('./pages/contracts/index');
                      return { Component, ErrorBoundary: PageErrorBoundary };
                    }),
                  },
                  {
                    // Before ':id', or a contract with this number could never be
                    // reached — the dynamic segment would swallow the path.
                    path: 'bulk-plan-change',
                    lazy: lazyWithRetry(async () => {
                      const { default: Component } = await import('./pages/contracts/bulk-plan-change');
                      return { Component, ErrorBoundary: PageErrorBoundary };
                    }),
                  },
                  {
                    path: ':id',
                    lazy: lazyWithRetry(async () => {
                      const { default: Component } = await import('./pages/contracts/[id]');
                      return { Component, ErrorBoundary: PageErrorBoundary };
                    }),
                  },
                ],
              },
              {
                path: 'commissions',
                children: [
                  {
                    index: true,
                    lazy: lazyWithRetry(async () => {
                      const { default: Component } = await import('./pages/commissions/index');
                      return { Component, ErrorBoundary: PageErrorBoundary };
                    }),
                  },
                  {
                    path: 'entries',
                    lazy: lazyWithRetry(async () => {
                      const { default: Component } = await import('./pages/commissions/entries');
                      return { Component, ErrorBoundary: PageErrorBoundary };
                    }),
                  },
                  {
                    path: 'paid-to-previous-seller',
                    lazy: lazyWithRetry(async () => {
                      const { default: Page } = await import('./pages/commissions/paid-to-previous-seller');
                      const Component = () => (
                        <RequirePermission module={MODULE_KEYS.COMMISSIONS} action="configure">
                          <Page />
                        </RequirePermission>
                      );
                      return { Component, ErrorBoundary: PageErrorBoundary };
                    }),
                  },
                ],
              },
              {
                path: 'banking',
                lazy: lazyWithRetry(async () => {
                  const { default: Component } = await import('./pages/banking/index');
                  return { Component, ErrorBoundary: PageErrorBoundary };
                }),
              },
              {
                path: 'bank-verification',
                lazy: lazyWithRetry(async () => {
                  const { default: Component } = await import('./pages/bank-verification/index');
                  return { Component, ErrorBoundary: PageErrorBoundary };
                }),
              },
              {
                // Supervisor-facing: the countersignature queue.
                path: 'agent-agreements',
                lazy: lazyWithRetry(async () => {
                  const { default: Component } = await import('./pages/agent-agreements/index');
                  return { Component, ErrorBoundary: PageErrorBoundary };
                }),
              },
              {
                // Marketing's queue: agent invites held for approval (ADR-0081).
                path: 'agent-invite-approvals',
                lazy: lazyWithRetry(async () => {
                  const { default: Component } = await import('./pages/agent-invite-approvals/index');
                  return { Component, ErrorBoundary: PageErrorBoundary };
                }),
              },
              {
                path: 'transactions',
                children: [
                  {
                    index: true,
                    lazy: lazyWithRetry(async () => {
                      const { default: Component } = await import('./pages/transactions/index');
                      return { Component, ErrorBoundary: PageErrorBoundary };
                    }),
                  },
                  {
                    path: 'bank-statements',
                    children: [
                      {
                        index: true,
                        lazy: lazyWithRetry(async () => {
                          const { default: Component } = await import('./pages/transactions/bank-statements/index');
                          return { Component, ErrorBoundary: PageErrorBoundary };
                        }),
                      },
                      {
                        path: ':id',
                        lazy: lazyWithRetry(async () => {
                          const { default: Component } = await import('./pages/transactions/bank-statements/[id]');
                          return { Component, ErrorBoundary: PageErrorBoundary };
                        }),
                      },
                    ],
                  },
                  {
                    path: 'reconciliation',
                    lazy: lazyWithRetry(async () => {
                      const { default: Component } = await import('./pages/transactions/reconciliation/index');
                      return { Component, ErrorBoundary: PageErrorBoundary };
                    }),
                  },
                  {
                    // Before ':id', which would otherwise take 'momo' as a payment id.
                    path: 'momo',
                    lazy: lazyWithRetry(async () => {
                      const { default: Component } = await import('./pages/transactions/momo/index');
                      return { Component, ErrorBoundary: PageErrorBoundary };
                    }),
                  },
                  {
                    path: ':id',
                    lazy: lazyWithRetry(async () => {
                      const { default: Component } = await import('./pages/transactions/[id]');
                      return { Component, ErrorBoundary: PageErrorBoundary };
                    }),
                  },
                ],
              },
              {
                path: 'stock',
                lazy: lazyWithRetry(async () => {
                  const { default: Component } = await import('./pages/stock/layout');
                  return { Component, ErrorBoundary: PageErrorBoundary };
                }),
                children: [
                  {
                    index: true,
                    lazy: lazyWithRetry(async () => {
                      const { default: Component } = await import('./pages/stock/index');
                      return { Component, ErrorBoundary: PageErrorBoundary };
                    }),
                  },
                  {
                    path: 'agents',
                    children: [
                      {
                        index: true,
                        lazy: lazyWithRetry(async () => {
                          const { default: Component } = await import('./pages/stock/agents/index');
                          return { Component, ErrorBoundary: PageErrorBoundary };
                        }),
                      },
                      {
                        path: 'new',
                        lazy: lazyWithRetry(async () => {
                          const { default: Component } = await import('./pages/stock/agents/new');
                          return { Component, ErrorBoundary: PageErrorBoundary };
                        }),
                      },
                      {
                        path: ':id',
                        lazy: lazyWithRetry(async () => {
                          const { default: Component } = await import('./pages/stock/agents/[id]');
                          return { Component, ErrorBoundary: PageErrorBoundary };
                        }),
                      },
                    ],
                  },
                  {
                    path: 'stocking-points',
                    children: [
                      {
                        index: true,
                        lazy: lazyWithRetry(async () => {
                          const { default: Component } = await import('./pages/stock/stocking-points/index');
                          return { Component, ErrorBoundary: PageErrorBoundary };
                        }),
                      },
                      {
                        path: ':id',
                        lazy: lazyWithRetry(async () => {
                          const { default: Component } = await import('./pages/stock/stocking-points/[id]');
                          return { Component, ErrorBoundary: PageErrorBoundary };
                        }),
                      },
                      {
                        path: ':id/import-devices',
                        lazy: lazyWithRetry(async () => {
                          const { default: Component } = await import('./pages/stock/stocking-points/import-devices');
                          return { Component, ErrorBoundary: PageErrorBoundary };
                        }),
                      },
                    ],
                  },
                  {
                    // Gated on its own grant: who sees what the business buys is
                    // an organization's choice, not a side effect of stock:create.
                    path: 'purchase-orders',
                    children: [
                      {
                        index: true,
                        lazy: lazyWithRetry(async () => {
                          const { default: Page } = await import('./pages/stock/purchase-orders/index');
                          const Component = () => (
                            <RequirePermission module={MODULE_KEYS.STOCK} action="purchase_orders">
                              <Page />
                            </RequirePermission>
                          );
                          return { Component, ErrorBoundary: PageErrorBoundary };
                        }),
                      },
                      {
                        path: 'new',
                        lazy: lazyWithRetry(async () => {
                          const { default: Page } = await import('./pages/stock/purchase-orders/new');
                          const Component = () => (
                            <RequirePermission module={MODULE_KEYS.STOCK} action="purchase_orders">
                              <Page />
                            </RequirePermission>
                          );
                          return { Component, ErrorBoundary: PageErrorBoundary };
                        }),
                      },
                      {
                        path: ':id',
                        lazy: lazyWithRetry(async () => {
                          const { default: Page } = await import('./pages/stock/purchase-orders/[id]');
                          const Component = () => (
                            <RequirePermission module={MODULE_KEYS.STOCK} action="purchase_orders">
                              <Page />
                            </RequirePermission>
                          );
                          return { Component, ErrorBoundary: PageErrorBoundary };
                        }),
                      },
                    ],
                  },
                  {
                    path: 'stock-takes',
                    children: [
                      {
                        index: true,
                        lazy: lazyWithRetry(async () => {
                          const { default: Component } = await import('./pages/stock/stock-takes/index');
                          return { Component, ErrorBoundary: PageErrorBoundary };
                        }),
                      },
                      {
                        path: ':id',
                        lazy: lazyWithRetry(async () => {
                          const { default: Component } = await import('./pages/stock/stock-takes/[id]');
                          return { Component, ErrorBoundary: PageErrorBoundary };
                        }),
                      },
                    ],
                  },
                  {
                    path: 'transfers',
                    children: [
                      {
                        index: true,
                        lazy: lazyWithRetry(async () => {
                          const { default: Component } = await import('./pages/stock/transfers/index');
                          return { Component, ErrorBoundary: PageErrorBoundary };
                        }),
                      },
                      {
                        path: 'new',
                        lazy: lazyWithRetry(async () => {
                          const { default: Component } = await import('./pages/stock/transfers/new');
                          return { Component, ErrorBoundary: PageErrorBoundary };
                        }),
                      },
                      {
                        path: ':id',
                        lazy: lazyWithRetry(async () => {
                          const { default: Component } = await import('./pages/stock/transfers/[id]');
                          return { Component, ErrorBoundary: PageErrorBoundary };
                        }),
                      },
                    ],
                  },
                  {
                    // A Return to Head Office (ADR-0088 in the backend). The
                    // server scopes who sees which: agents their own, Head
                    // Office every one.
                    path: 'returns-to-head-office',
                    children: [
                      {
                        index: true,
                        lazy: lazyWithRetry(async () => {
                          const { default: Component } = await import('./pages/stock/returns-to-head-office/index');
                          return { Component, ErrorBoundary: PageErrorBoundary };
                        }),
                      },
                      {
                        path: 'new',
                        lazy: lazyWithRetry(async () => {
                          const { default: Component } = await import('./pages/stock/returns-to-head-office/new');
                          return { Component, ErrorBoundary: PageErrorBoundary };
                        }),
                      },
                      {
                        path: ':id',
                        lazy: lazyWithRetry(async () => {
                          const { default: Component } = await import('./pages/stock/returns-to-head-office/[id]');
                          return { Component, ErrorBoundary: PageErrorBoundary };
                        }),
                      },
                    ],
                  },
                  {
                    path: 'sales',
                    children: [
                      {
                        index: true,
                        lazy: lazyWithRetry(async () => {
                          const { default: Component } = await import('./pages/stock/sales/index');
                          return { Component, ErrorBoundary: PageErrorBoundary };
                        }),
                      },
                      {
                        path: ':id',
                        lazy: lazyWithRetry(async () => {
                          const { default: Component } = await import('./pages/stock/sales/[id]');
                          return { Component, ErrorBoundary: PageErrorBoundary };
                        }),
                      },
                    ],
                  },
                  {
                    path: 'serial-lookup',
                    lazy: lazyWithRetry(async () => {
                      const { default: Component } = await import('./pages/stock/serial-lookup/index');
                      return { Component, ErrorBoundary: PageErrorBoundary };
                    }),
                  },
                  {
                    path: 'warranty',
                    lazy: lazyWithRetry(async () => {
                      const { default: Component } = await import('./pages/stock/warranty/index');
                      return { Component, ErrorBoundary: PageErrorBoundary };
                    }),
                  },
                  {
                    path: 'parts',
                    lazy: lazyWithRetry(async () => {
                      const { default: Page } = await import('./pages/stock/parts');
                      const Component = () => (
                        <RequirePermission module={MODULE_KEYS.STOCK} action="manage_parts">
                          <Page />
                        </RequirePermission>
                      );
                      return { Component, ErrorBoundary: PageErrorBoundary };
                    }),
                  },
                  {
                    // Procurement's screen, so it is gated on the grant that sources
                    // parts rather than on the workshop module that raises the asks.
                    path: 'part-requests',
                    lazy: lazyWithRetry(async () => {
                      const { default: Page } = await import('./pages/stock/part-requests');
                      const Component = () => (
                        <RequirePermission module={MODULE_KEYS.STOCK} action="source_parts">
                          <Page />
                        </RequirePermission>
                      );
                      return { Component, ErrorBoundary: PageErrorBoundary };
                    }),
                  },
                  {
                    // Marketing's screen, on its own grant: it carries its own
                    // sale lookup and agent list, so it needs no stock:view.
                    path: 'seller-corrections',
                    lazy: lazyWithRetry(async () => {
                      const { default: Page } = await import('./pages/stock/seller-corrections/index');
                      const Component = () => (
                        <RequirePermission module={MODULE_KEYS.STOCK} action="correct_seller">
                          <Page />
                        </RequirePermission>
                      );
                      return { Component, ErrorBoundary: PageErrorBoundary };
                    }),
                  },
                  {
                    // The warranty claims report. It lives with Stock, but it is
                    // a report: the page checks its report grant, not stock.
                    path: 'warranty-claims',
                    lazy: lazyWithRetry(async () => {
                      const { default: Component } = await import('./pages/reports/warranty');
                      return { Component, ErrorBoundary: PageErrorBoundary };
                    }),
                  },
                  // Moved to the Reports module; old links follow it.
                  { path: 'reports', loader: () => redirect('/reports/stock') },
                ],
              },
              {
                path: 'tasks',
                lazy: lazyWithRetry(async () => {
                  const { default: Component } = await import('./pages/tasks/index');
                  return { Component, ErrorBoundary: PageErrorBoundary };
                }),
              },
              {
                path: 'workshop',
                children: [
                  {
                    index: true,
                    lazy: lazyWithRetry(async () => {
                      const { default: Page } = await import('./pages/workshop/index');
                      const Component = () => (
                        <RequirePermission module={MODULE_KEYS.WORKSHOP}>
                          <Page />
                        </RequirePermission>
                      );
                      return { Component, ErrorBoundary: PageErrorBoundary };
                    }),
                  },
                  {
                    // Before the :id route, so "parts" is not read as a job id.
                    path: 'parts',
                    lazy: lazyWithRetry(async () => {
                      const { default: Page } = await import('./pages/workshop/parts');
                      const Component = () => (
                        <RequirePermission module={MODULE_KEYS.WORKSHOP}>
                          <Page />
                        </RequirePermission>
                      );
                      return { Component, ErrorBoundary: PageErrorBoundary };
                    }),
                  },
                  {
                    // Before the :id route, for the same reason as "parts".
                    path: 'stats',
                    lazy: lazyWithRetry(async () => {
                      const { default: Page } = await import('./pages/workshop/stats');
                      const Component = () => (
                        <RequirePermission module={MODULE_KEYS.WORKSHOP}>
                          <Page />
                        </RequirePermission>
                      );
                      return { Component, ErrorBoundary: PageErrorBoundary };
                    }),
                  },
                  {
                    path: ':id',
                    lazy: lazyWithRetry(async () => {
                      const { default: Page } = await import('./pages/workshop/[id]');
                      const Component = () => (
                        <RequirePermission module={MODULE_KEYS.WORKSHOP}>
                          <Page />
                        </RequirePermission>
                      );
                      return { Component, ErrorBoundary: PageErrorBoundary };
                    }),
                  },
                ],
              },
              // SMS Campaigns (backend PRD #855): one message to many customers.
              {
                path: 'sms-campaigns',
                children: [
                  {
                    index: true,
                    lazy: lazyWithRetry(async () => {
                      const { default: Page } = await import('./pages/sms-campaigns/index');
                      const Component = () => (
                        <RequirePermission module={MODULE_KEYS.SMS_CAMPAIGNS} action="view">
                          <Page />
                        </RequirePermission>
                      );
                      return { Component, ErrorBoundary: PageErrorBoundary };
                    }),
                  },
                  // Before :id, so these are not read as a campaign id.
                  // Agent messages sit beside Customer SMS under Campaigns, on their own grant.
                  {
                    path: 'agent-messages',
                    lazy: lazyWithRetry(async () => {
                      const { default: Page } = await import('./pages/sales-app-messages/index');
                      const Component = () => (
                        <RequirePermission module={MODULE_KEYS.SALES_APP_MESSAGES} action="view">
                          <Page />
                        </RequirePermission>
                      );
                      return { Component, ErrorBoundary: PageErrorBoundary };
                    }),
                  },
                  {
                    path: 'new',
                    lazy: lazyWithRetry(async () => {
                      const { default: Page } = await import('./pages/sms-campaigns/new');
                      const Component = () => (
                        <RequirePermission module={MODULE_KEYS.SMS_CAMPAIGNS} action="create">
                          <Page />
                        </RequirePermission>
                      );
                      return { Component, ErrorBoundary: PageErrorBoundary };
                    }),
                  },
                  {
                    path: 'opt-outs',
                    lazy: lazyWithRetry(async () => {
                      const { default: Page } = await import('./pages/sms-campaigns/opt-outs');
                      const Component = () => (
                        <RequirePermission module={MODULE_KEYS.SMS_CAMPAIGNS} action="view">
                          <Page />
                        </RequirePermission>
                      );
                      return { Component, ErrorBoundary: PageErrorBoundary };
                    }),
                  },
                  {
                    path: ':id',
                    lazy: lazyWithRetry(async () => {
                      const { default: Page } = await import('./pages/sms-campaigns/[id]');
                      const Component = () => (
                        <RequirePermission module={MODULE_KEYS.SMS_CAMPAIGNS} action="view">
                          <Page />
                        </RequirePermission>
                      );
                      return { Component, ErrorBoundary: PageErrorBoundary };
                    }),
                  },
                  {
                    path: ':id/edit',
                    lazy: lazyWithRetry(async () => {
                      const { default: Page } = await import('./pages/sms-campaigns/edit');
                      const Component = () => (
                        <RequirePermission module={MODULE_KEYS.SMS_CAMPAIGNS} action="create">
                          <Page />
                        </RequirePermission>
                      );
                      return { Component, ErrorBoundary: PageErrorBoundary };
                    }),
                  },
                ],
              },
              {
                path: 'approvals',
                children: [
                  {
                    index: true,
                    lazy: lazyWithRetry(async () => {
                      const { default: Component } = await import('./pages/approvals/index');
                      return { Component, ErrorBoundary: PageErrorBoundary };
                    }),
                  },
                  {
                    path: ':id',
                    lazy: lazyWithRetry(async () => {
                      const { default: Component } = await import('./pages/approvals/[id]');
                      return { Component, ErrorBoundary: PageErrorBoundary };
                    }),
                  },
                ],
              },
            ],
          },

          // The Stock module: head office's stock pages. The pages it shares with
          // the CRM keep their /crm/stock addresses (see app-modules.ts). The
          // stock reports are mounted here too, so reading one does not leave
          // the module; each still checks its own report grant.
          {
            path: 'stock',
            children: [
              {
                index: true,
                lazy: lazyWithRetry(async () => {
                  const { default: Page } = await import('./pages/stock-module/dashboard');
                  const Component = () => (
                    <RequirePermission module={MODULE_KEYS.STOCK} action={STOCK_MODULE_ACTION}>
                      <Page />
                    </RequirePermission>
                  );
                  return { Component, ErrorBoundary: PageErrorBoundary };
                }),
              },
              {
                path: 'item-register',
                lazy: lazyWithRetry(async () => {
                  const { default: Page } = await import('./pages/stock-module/item-register');
                  const Component = () => (
                    <RequirePermission module={MODULE_KEYS.STOCK} action={STOCK_MODULE_ACTION}>
                      <Page />
                    </RequirePermission>
                  );
                  return { Component, ErrorBoundary: PageErrorBoundary };
                }),
              },
              {
                path: 'stock-card',
                lazy: lazyWithRetry(async () => {
                  const { default: Page } = await import('./pages/stock-module/stock-card');
                  const Component = () => (
                    <RequirePermission module={MODULE_KEYS.STOCK} action={STOCK_MODULE_ACTION}>
                      <Page />
                    </RequirePermission>
                  );
                  return { Component, ErrorBoundary: PageErrorBoundary };
                }),
              },
              {
                path: 'capture',
                lazy: lazyWithRetry(async () => {
                  const { default: Page } = await import('./pages/stock-module/capture');
                  const Component = () => (
                    <RequirePermission module={MODULE_KEYS.STOCK} action={STOCK_MODULE_ACTION}>
                      <Page />
                    </RequirePermission>
                  );
                  return { Component, ErrorBoundary: PageErrorBoundary };
                }),
              },
              {
                path: 'stock-take',
                lazy: lazyWithRetry(async () => {
                  const { default: Page } = await import('./pages/stock-module/stock-take');
                  const Component = () => (
                    <RequirePermission module={MODULE_KEYS.STOCK} action={STOCK_MODULE_ACTION}>
                      <Page />
                    </RequirePermission>
                  );
                  return { Component, ErrorBoundary: PageErrorBoundary };
                }),
              },
              {
                path: 'count-sheet',
                lazy: lazyWithRetry(async () => {
                  const { default: Page } = await import('./pages/stock-module/count-sheet');
                  const Component = () => (
                    <RequirePermission module={MODULE_KEYS.STOCK} action={STOCK_MODULE_ACTION}>
                      <Page />
                    </RequirePermission>
                  );
                  return { Component, ErrorBoundary: PageErrorBoundary };
                }),
              },
              {
                path: 'reports/valuation',
                lazy: lazyWithRetry(async () => {
                  const { default: Component } = await import('./pages/reports/stock-valuation');
                  return { Component, ErrorBoundary: PageErrorBoundary };
                }),
              },
              {
                path: 'reports/by-tier',
                lazy: lazyWithRetry(async () => {
                  const { default: Component } = await import('./pages/reports/stock-by-tier');
                  return { Component, ErrorBoundary: PageErrorBoundary };
                }),
              },
              {
                path: 'reports/sales',
                lazy: lazyWithRetry(async () => {
                  const { default: Component } = await import('./pages/reports/stock-sales');
                  return { Component, ErrorBoundary: PageErrorBoundary };
                }),
              },
              {
                path: 'reports/stock',
                lazy: lazyWithRetry(async () => {
                  const { default: Component } = await import('./pages/stock/reports/index');
                  return { Component, ErrorBoundary: PageErrorBoundary };
                }),
              },
            ],
          },

          // The HR module (ADR-0060, ADR-0061 in the backend). Every page needs
          // its own HR child grant, which a SuperAdmin does not get
          // automatically. The dashboard opens for any HR child and shows a
          // tile only for the areas the person can view.
          {
            path: 'hr',
            children: [
              {
                index: true,
                lazy: lazyWithRetry(async () => {
                  const { default: Page } = await import('./pages/hr/dashboard');
                  const Component = () => (
                    <RequirePermission module={HR_CHILD_MODULES} action="view">
                      <Page />
                    </RequirePermission>
                  );
                  return { Component, ErrorBoundary: PageErrorBoundary };
                }),
              },
              {
                path: 'employees',
                children: [
                  {
                    index: true,
                    lazy: lazyWithRetry(async () => {
                      const { default: Page } = await import('./pages/hr/employees/index');
                      const Component = () => (
                        <RequirePermission module={MODULE_KEYS.HR_WORKFORCE} action="view">
                          <Page />
                        </RequirePermission>
                      );
                      return { Component, ErrorBoundary: PageErrorBoundary };
                    }),
                  },
                  {
                    path: ':id',
                    lazy: lazyWithRetry(async () => {
                      const { default: Page } = await import('./pages/hr/employees/[id]');
                      const Component = () => (
                        <RequirePermission module={MODULE_KEYS.HR_WORKFORCE} action="view">
                          <Page />
                        </RequirePermission>
                      );
                      return { Component, ErrorBoundary: PageErrorBoundary };
                    }),
                  },
                ],
              },
              {
                path: 'teams-positions',
                lazy: lazyWithRetry(async () => {
                  const { default: Page } = await import('./pages/hr/teams-positions');
                  const Component = () => (
                    <RequirePermission module={MODULE_KEYS.HR_DEMOGRAPHICS} action="view">
                      <Page />
                    </RequirePermission>
                  );
                  return { Component, ErrorBoundary: PageErrorBoundary };
                }),
              },
              {
                path: 'performance',
                lazy: lazyWithRetry(async () => {
                  const { default: Page } = await import('./pages/hr/performance/index');
                  const Component = () => (
                    <RequirePermission module={MODULE_KEYS.HR_PERFORMANCE} action="view">
                      <Page />
                    </RequirePermission>
                  );
                  return { Component, ErrorBoundary: PageErrorBoundary };
                }),
              },
              {
                path: 'cases',
                children: [
                  {
                    index: true,
                    lazy: lazyWithRetry(async () => {
                      const { default: Page } = await import('./pages/hr/cases/index');
                      const Component = () => (
                        <RequirePermission module={MODULE_KEYS.HR_CASES} action="view">
                          <Page />
                        </RequirePermission>
                      );
                      return { Component, ErrorBoundary: PageErrorBoundary };
                    }),
                  },
                  {
                    path: ':id',
                    lazy: lazyWithRetry(async () => {
                      const { default: Page } = await import('./pages/hr/cases/[id]');
                      const Component = () => (
                        <RequirePermission module={MODULE_KEYS.HR_CASES} action="view">
                          <Page />
                        </RequirePermission>
                      );
                      return { Component, ErrorBoundary: PageErrorBoundary };
                    }),
                  },
                ],
              },
              {
                path: 'training',
                lazy: lazyWithRetry(async () => {
                  const { default: Page } = await import('./pages/hr/training/index');
                  const Component = () => (
                    <RequirePermission module={MODULE_KEYS.HR_TRAINING} action="view">
                      <Page />
                    </RequirePermission>
                  );
                  return { Component, ErrorBoundary: PageErrorBoundary };
                }),
              },
              {
                path: 'recruitment',
                children: [
                  {
                    index: true,
                    lazy: lazyWithRetry(async () => {
                      const { default: Page } = await import('./pages/hr/recruitment/index');
                      const Component = () => (
                        <RequirePermission module={MODULE_KEYS.HR_RECRUITMENT} action="view">
                          <Page />
                        </RequirePermission>
                      );
                      return { Component, ErrorBoundary: PageErrorBoundary };
                    }),
                  },
                  {
                    path: 'vacancies/:id',
                    lazy: lazyWithRetry(async () => {
                      const { default: Page } = await import('./pages/hr/recruitment/[id]');
                      const Component = () => (
                        <RequirePermission module={MODULE_KEYS.HR_RECRUITMENT} action="view">
                          <Page />
                        </RequirePermission>
                      );
                      return { Component, ErrorBoundary: PageErrorBoundary };
                    }),
                  },
                ],
              },
              {
                path: 'safety',
                children: [
                  {
                    index: true,
                    lazy: lazyWithRetry(async () => {
                      const { default: Page } = await import('./pages/hr/safety/index');
                      const Component = () => (
                        <RequirePermission module={MODULE_KEYS.HR_SAFETY} action="view">
                          <Page />
                        </RequirePermission>
                      );
                      return { Component, ErrorBoundary: PageErrorBoundary };
                    }),
                  },
                  {
                    path: 'incidents/:id',
                    lazy: lazyWithRetry(async () => {
                      const { default: Page } = await import('./pages/hr/safety/[id]');
                      const Component = () => (
                        <RequirePermission module={MODULE_KEYS.HR_SAFETY} action="view">
                          <Page />
                        </RequirePermission>
                      );
                      return { Component, ErrorBoundary: PageErrorBoundary };
                    }),
                  },
                ],
              },
              {
                path: 'subcontractors',
                children: [
                  {
                    index: true,
                    lazy: lazyWithRetry(async () => {
                      const { default: Page } = await import('./pages/hr/subcontractors/index');
                      const Component = () => (
                        <RequirePermission module={MODULE_KEYS.HR_SUBCONTRACTORS} action="view">
                          <Page />
                        </RequirePermission>
                      );
                      return { Component, ErrorBoundary: PageErrorBoundary };
                    }),
                  },
                  {
                    path: ':id',
                    lazy: lazyWithRetry(async () => {
                      const { default: Page } = await import('./pages/hr/subcontractors/[id]');
                      const Component = () => (
                        <RequirePermission module={MODULE_KEYS.HR_SUBCONTRACTORS} action="view">
                          <Page />
                        </RequirePermission>
                      );
                      return { Component, ErrorBoundary: PageErrorBoundary };
                    }),
                  },
                ],
              },
              {
                path: 'attendance',
                children: [
                  {
                    index: true,
                    lazy: lazyWithRetry(async () => {
                      const { default: Page } = await import('./pages/hr/attendance/index');
                      const Component = () => (
                        <RequirePermission module={MODULE_KEYS.HR_ATTENDANCE} action="view">
                          <Page />
                        </RequirePermission>
                      );
                      return { Component, ErrorBoundary: PageErrorBoundary };
                    }),
                  },
                ],
              },
              {
                path: 'payroll',
                children: [
                  {
                    index: true,
                    lazy: lazyWithRetry(async () => {
                      const { default: Page } = await import('./pages/hr/payroll/index');
                      const Component = () => (
                        <RequirePermission module={MODULE_KEYS.HR_PAYROLL} action="view">
                          <Page />
                        </RequirePermission>
                      );
                      return { Component, ErrorBoundary: PageErrorBoundary };
                    }),
                  },
                  {
                    path: ':id',
                    lazy: lazyWithRetry(async () => {
                      const { default: Page } = await import('./pages/hr/payroll/[id]');
                      const Component = () => (
                        <RequirePermission module={MODULE_KEYS.HR_PAYROLL} action="view">
                          <Page />
                        </RequirePermission>
                      );
                      return { Component, ErrorBoundary: PageErrorBoundary };
                    }),
                  },
                ],
              },
              {
                path: 'esg',
                children: [
                  {
                    index: true,
                    lazy: lazyWithRetry(async () => {
                      const { default: Page } = await import('./pages/hr/esg/index');
                      const Component = () => (
                        <RequirePermission module={MODULE_KEYS.HR_ESG} action="view">
                          <Page />
                        </RequirePermission>
                      );
                      return { Component, ErrorBoundary: PageErrorBoundary };
                    }),
                  },
                  {
                    path: ':id',
                    lazy: lazyWithRetry(async () => {
                      const { default: Page } = await import('./pages/hr/esg/[id]');
                      const Component = () => (
                        <RequirePermission module={MODULE_KEYS.HR_ESG} action="view">
                          <Page />
                        </RequirePermission>
                      );
                      return { Component, ErrorBoundary: PageErrorBoundary };
                    }),
                  },
                ],
              },
              {
                path: 'grievances',
                children: [
                  {
                    index: true,
                    lazy: lazyWithRetry(async () => {
                      const { default: Page } = await import('./pages/hr/grievances/index');
                      const Component = () => (
                        <RequirePermission module={MODULE_KEYS.HR_GRIEVANCES} action="view">
                          <Page />
                        </RequirePermission>
                      );
                      return { Component, ErrorBoundary: PageErrorBoundary };
                    }),
                  },
                  {
                    path: 'report',
                    lazy: lazyWithRetry(async () => {
                      const { default: Page } = await import('./pages/hr/grievances/report');
                      const Component = () => (
                        <RequirePermission module={MODULE_KEYS.HR_GRIEVANCES} action="export">
                          <Page />
                        </RequirePermission>
                      );
                      return { Component, ErrorBoundary: PageErrorBoundary };
                    }),
                  },
                  {
                    path: 'settings',
                    lazy: lazyWithRetry(async () => {
                      const { default: Page } = await import('./pages/hr/grievances/settings');
                      const Component = () => (
                        <RequirePermission module={MODULE_KEYS.HR_GRIEVANCES} action="configure">
                          <Page />
                        </RequirePermission>
                      );
                      return { Component, ErrorBoundary: PageErrorBoundary };
                    }),
                  },
                  {
                    path: ':id',
                    lazy: lazyWithRetry(async () => {
                      const { default: Page } = await import('./pages/hr/grievances/[id]');
                      const Component = () => (
                        <RequirePermission module={MODULE_KEYS.HR_GRIEVANCES} action="view">
                          <Page />
                        </RequirePermission>
                      );
                      return { Component, ErrorBoundary: PageErrorBoundary };
                    }),
                  },
                ],
              },
            ],
          },

          // The Reports module. Each report checks `reports:view` and its own
          // report grant itself (useCanViewReport).
          {
            path: 'reports',
            children: [
              {
                index: true,
                lazy: lazyWithRetry(async () => {
                  const { default: Component } = await import('./pages/reports/index');
                  return { Component, ErrorBoundary: PageErrorBoundary };
                }),
              },
              {
                path: 'stock',
                lazy: lazyWithRetry(async () => {
                  const { default: Component } = await import('./pages/stock/reports/index');
                  return { Component, ErrorBoundary: PageErrorBoundary };
                }),
              },
              {
                path: 'stock-sales',
                lazy: lazyWithRetry(async () => {
                  const { default: Component } = await import('./pages/reports/stock-sales');
                  return { Component, ErrorBoundary: PageErrorBoundary };
                }),
              },
              {
                path: 'stock-valuation',
                lazy: lazyWithRetry(async () => {
                  const { default: Component } = await import('./pages/reports/stock-valuation');
                  return { Component, ErrorBoundary: PageErrorBoundary };
                }),
              },
              {
                path: 'stock-by-tier',
                lazy: lazyWithRetry(async () => {
                  const { default: Component } = await import('./pages/reports/stock-by-tier');
                  return { Component, ErrorBoundary: PageErrorBoundary };
                }),
              },
              {
                path: 'finance-workbooks',
                lazy: lazyWithRetry(async () => {
                  const { default: Component } = await import('./pages/reports/finance-workbooks');
                  return { Component, ErrorBoundary: PageErrorBoundary };
                }),
              },
              {
                path: 'agent-hierarchy',
                lazy: lazyWithRetry(async () => {
                  const { default: Component } = await import('./pages/reports/agent-hierarchy');
                  return { Component, ErrorBoundary: PageErrorBoundary };
                }),
              },
              {
                path: 'customer-book',
                lazy: lazyWithRetry(async () => {
                  const { default: Component } = await import('./pages/reports/customer-book');
                  return { Component, ErrorBoundary: PageErrorBoundary };
                }),
              },
              {
                path: 'sales-contribution',
                lazy: lazyWithRetry(async () => {
                  const { default: Component } = await import(
                    './pages/reports/sales-contribution'
                  );
                  return { Component, ErrorBoundary: PageErrorBoundary };
                }),
              },
              {
                path: 'customer-demographics',
                lazy: lazyWithRetry(async () => {
                  const { default: Component } = await import(
                    './pages/reports/customer-demographics'
                  );
                  return { Component, ErrorBoundary: PageErrorBoundary };
                }),
              },
              {
                path: 'digital-channels',
                lazy: lazyWithRetry(async () => {
                  const { default: Component } = await import('./pages/reports/digital-channels');
                  return { Component, ErrorBoundary: PageErrorBoundary };
                }),
              },
              // Warranty claims sits under Stock; old links follow it.
              { path: 'warranty', loader: () => redirect('/crm/stock/warranty-claims') },
              {
                path: 'edf',
                children: [
                  {
                    index: true,
                    lazy: lazyWithRetry(async () => {
                      const { default: Component } = await import('./pages/reports/edf');
                      return { Component, ErrorBoundary: PageErrorBoundary };
                    }),
                  },
                  {
                    // A report of its own, read off the same run of EDF's
                    // pipeline: the auditor's monthly debtor ageing.
                    path: 'debtor-ageing',
                    // Hidden: an old link or bookmark lands on the EDF report
                    // instead. See edf-debtor-ageing-disabled.ts.
                    loader: () => (EDF_DEBTOR_AGEING_ENABLED ? null : redirect('/reports/edf')),
                    lazy: lazyWithRetry(async () => {
                      const { default: Component } = await import(
                        './pages/reports/edf-debtor-ageing'
                      );
                      return { Component, ErrorBoundary: PageErrorBoundary };
                    }),
                  },
                ],
              },
              {
                path: 'receivables',
                children: [
                  {
                    index: true,
                    // Hidden: Receivables ageing counts the book the KES way,
                    // and Finance reports to EDF's standards. An old link or
                    // bookmark lands on the Reports overview instead. See
                    // receivables-ageing-disabled.ts.
                    loader: () => (RECEIVABLES_AGEING_ENABLED ? null : redirect('/reports')),
                    lazy: lazyWithRetry(async () => {
                      const { default: Component } = await import('./pages/reports/receivables');
                      return { Component, ErrorBoundary: PageErrorBoundary };
                    }),
                  },
                  {
                    // A sub-report of Receivables: the same month-end and the
                    // same contracts, cut to the ones that ended badly.
                    path: 'bad-debtors',
                    lazy: lazyWithRetry(async () => {
                      const { default: Component } = await import('./pages/reports/bad-debtors');
                      return { Component, ErrorBoundary: PageErrorBoundary };
                    }),
                  },
                ],
              },
            ],
          },

          // The in-app manual. Deliberately ungated: every signed-in user can
          // read it, and each article filters itself by the modules its reader
          // can actually reach.
          {
            path: 'help',
            lazy: lazyWithRetry(async () => {
              const { default: Component } = await import('./pages/help/layout');
              return { Component, ErrorBoundary: PageErrorBoundary };
            }),
            children: [
              {
                index: true,
                lazy: lazyWithRetry(async () => {
                  const { default: Component } = await import('./pages/help/index');
                  return { Component, ErrorBoundary: PageErrorBoundary };
                }),
              },
              {
                path: 'whats-new',
                lazy: lazyWithRetry(async () => {
                  const { default: Component } = await import('./pages/help/whats-new');
                  return { Component, ErrorBoundary: PageErrorBoundary };
                }),
              },
              {
                // Splat: an article's slug is its path under docs/manual.
                path: '*',
                lazy: lazyWithRetry(async () => {
                  const { default: Component } = await import('./pages/help/article');
                  return { Component, ErrorBoundary: PageErrorBoundary };
                }),
              },
            ],
          },

          {
            path: 'settings',
            lazy: lazyWithRetry(async () => {
              const { default: Component } = await import('./pages/settings/layout');
              return { Component, ErrorBoundary: PageErrorBoundary };
            }),
            children: [
              {
                index: true,
                lazy: lazyWithRetry(async () => {
                  const { default: Component } = await import('./pages/settings/index');
                  return { Component, ErrorBoundary: PageErrorBoundary };
                }),
              },
              {
                // Agent-facing: read, confirm details, sign. It lives here
                // rather than in the main sidebar because it is a thing you do
                // once about yourself, not somewhere you work.
                path: 'my-agreement',
                lazy: lazyWithRetry(async () => {
                  const { default: Component } = await import('./pages/agreements/index');
                  return { Component, ErrorBoundary: PageErrorBoundary };
                }),
              },
              {
                path: 'organization',
                lazy: lazyWithRetry(async () => {
                  const { default: Component } = await import('./pages/settings/organization');
                  return { Component, ErrorBoundary: PageErrorBoundary };
                }),
              },
              {
                path: 'security',
                lazy: lazyWithRetry(async () => {
                  const { default: Component } = await import('./pages/settings/security');
                  return { Component, ErrorBoundary: PageErrorBoundary };
                }),
              },
              {
                path: 'access-requests',
                lazy: lazyWithRetry(async () => {
                  const { default: Component } = await import('./pages/settings/access-requests');
                  return { Component, ErrorBoundary: PageErrorBoundary };
                }),
              },
              {
                path: 'members',
                lazy: lazyWithRetry(async () => {
                  const { default: Component } = await import('./pages/settings/members');
                  return { Component, ErrorBoundary: PageErrorBoundary };
                }),
              },
              {
                path: 'dcc-bulk-invite',
                lazy: lazyWithRetry(async () => {
                  const { default: Component } = await import('./pages/settings/dcc-bulk-invite');
                  return { Component, ErrorBoundary: PageErrorBoundary };
                }),
              },
              {
                path: 'disputes',
                lazy: lazyWithRetry(async () => {
                  const { default: Component } = await import('./pages/settings/disputes');
                  return { Component, ErrorBoundary: PageErrorBoundary };
                }),
              },
              {
                path: 'roles',
                lazy: lazyWithRetry(async () => {
                  const { default: Component } = await import('./pages/settings/roles');
                  return { Component, ErrorBoundary: PageErrorBoundary };
                }),
              },
              {
                path: 'modules',
                lazy: lazyWithRetry(async () => {
                  const { default: Component } = await import('./pages/settings/modules');
                  return { Component, ErrorBoundary: PageErrorBoundary };
                }),
              },
              {
                path: 'departments',
                lazy: lazyWithRetry(async () => {
                  const { default: Component } = await import('./pages/settings/departments');
                  return { Component, ErrorBoundary: PageErrorBoundary };
                }),
              },
              {
                path: 'lead-config',
                lazy: lazyWithRetry(async () => {
                  const { default: Component } = await import('./pages/settings/lead-config');
                  return { Component, ErrorBoundary: PageErrorBoundary };
                }),
              },
              {
                path: 'rate-plans',
                lazy: lazyWithRetry(async () => {
                  const { default: Component } = await import('./pages/settings/rate-plans');
                  return { Component, ErrorBoundary: PageErrorBoundary };
                }),
              },
              {
                path: 'delinquency-policy',
                lazy: lazyWithRetry(async () => {
                  const { default: Component } = await import('./pages/settings/delinquency-policy');
                  return { Component, ErrorBoundary: PageErrorBoundary };
                }),
              },
              {
                path: 'payment-rating-policy',
                lazy: lazyWithRetry(async () => {
                  const { default: Component } = await import('./pages/settings/payment-rating-policy');
                  return { Component, ErrorBoundary: PageErrorBoundary };
                }),
              },
              {
                path: 'reallocation-policy',
                lazy: lazyWithRetry(async () => {
                  const { default: Component } = await import(
                    './pages/settings/reallocation-policy'
                  );
                  return { Component, ErrorBoundary: PageErrorBoundary };
                }),
              },
              {
                path: 'workshop-policy',
                lazy: lazyWithRetry(async () => {
                  const { default: Component } = await import('./pages/settings/workshop-policy');
                  return { Component, ErrorBoundary: PageErrorBoundary };
                }),
              },
              {
                path: 'permissions',
                lazy: lazyWithRetry(async () => {
                  const { default: Component } = await import('./pages/settings/permissions');
                  return { Component, ErrorBoundary: PageErrorBoundary };
                }),
              },
              {
                path: 'suppliers',
                lazy: lazyWithRetry(async () => {
                  const { default: Component } = await import('./pages/settings/suppliers');
                  return { Component, ErrorBoundary: PageErrorBoundary };
                }),
              },
              {
                path: 'shipping-companies',
                lazy: lazyWithRetry(async () => {
                  const { default: Component } = await import('./pages/settings/shipping-companies');
                  return { Component, ErrorBoundary: PageErrorBoundary };
                }),
              },
              {
                path: 'products',
                lazy: lazyWithRetry(async () => {
                  const { default: Component } = await import('./pages/settings/products');
                  return { Component, ErrorBoundary: PageErrorBoundary };
                }),
              },
              {
                path: 'product-categories',
                lazy: lazyWithRetry(async () => {
                  const { default: Component } = await import('./pages/settings/product-categories');
                  return { Component, ErrorBoundary: PageErrorBoundary };
                }),
              },
              {
                path: 'product-brands',
                lazy: lazyWithRetry(async () => {
                  const { default: Component } = await import('./pages/settings/product-brands');
                  return { Component, ErrorBoundary: PageErrorBoundary };
                }),
              },
              {
                path: 'business-lines',
                lazy: lazyWithRetry(async () => {
                  const { default: Component } = await import('./pages/settings/business-lines');
                  return { Component, ErrorBoundary: PageErrorBoundary };
                }),
              },
              {
                path: 'item-categories',
                lazy: lazyWithRetry(async () => {
                  const { default: Component } = await import('./pages/settings/item-categories');
                  return { Component, ErrorBoundary: PageErrorBoundary };
                }),
              },
              {
                path: 'units-of-measure',
                lazy: lazyWithRetry(async () => {
                  const { default: Component } = await import('./pages/settings/units-of-measure');
                  return { Component, ErrorBoundary: PageErrorBoundary };
                }),
              },
              {
                path: 'stock-types',
                lazy: lazyWithRetry(async () => {
                  const { default: Component } = await import('./pages/settings/stock-types');
                  return { Component, ErrorBoundary: PageErrorBoundary };
                }),
              },
              {
                path: 'device-types',
                lazy: lazyWithRetry(async () => {
                  const { default: Component } = await import('./pages/settings/device-types');
                  return { Component, ErrorBoundary: PageErrorBoundary };
                }),
              },
              {
                path: 'fault-categories',
                lazy: lazyWithRetry(async () => {
                  const { default: Component } = await import(
                    './pages/settings/workshop-faults/categories'
                  );
                  return { Component, ErrorBoundary: PageErrorBoundary };
                }),
              },
              {
                path: 'fault-types',
                lazy: lazyWithRetry(async () => {
                  const { default: Component } = await import(
                    './pages/settings/workshop-faults/types'
                  );
                  return { Component, ErrorBoundary: PageErrorBoundary };
                }),
              },
              // Commission settings sub-layout
              {
                path: 'commissions',
                lazy: lazyWithRetry(async () => {
                  const { default: Component } = await import('./pages/settings/commissions/layout');
                  return { Component, ErrorBoundary: PageErrorBoundary };
                }),
                children: [
                  {
                    index: true,
                    lazy: lazyWithRetry(async () => {
                      const { default: Component } = await import('./pages/settings/agent-categories');
                      return { Component, ErrorBoundary: PageErrorBoundary };
                    }),
                  },
                  {
                    path: 'types',
                    lazy: lazyWithRetry(async () => {
                      const { default: Component } = await import('./pages/settings/commission-types');
                      return { Component, ErrorBoundary: PageErrorBoundary };
                    }),
                  },
                  {
                    path: 'rules',
                    lazy: lazyWithRetry(async () => {
                      const { default: Component } = await import('./pages/settings/commission-rules');
                      return { Component, ErrorBoundary: PageErrorBoundary };
                    }),
                  },
                  {
                    path: 'policy',
                    lazy: lazyWithRetry(async () => {
                      const { default: Component } = await import('./pages/settings/commission-policy');
                      return { Component, ErrorBoundary: PageErrorBoundary };
                    }),
                  },
                ],
              },
              {
                path: 'audit-log',
                lazy: lazyWithRetry(async () => {
                  const { default: Component } = await import('./pages/settings/audit-log');
                  return { Component, ErrorBoundary: PageErrorBoundary };
                }),
              },
              {
                path: 'webhook-events',
                lazy: lazyWithRetry(async () => {
                  const { default: Component } = await import('./pages/settings/webhook-events');
                  return { Component, ErrorBoundary: PageErrorBoundary };
                }),
              },
              {
                path: 'sms-log',
                lazy: lazyWithRetry(async () => {
                  const { default: Component } = await import('./pages/settings/sms-log');
                  return { Component, ErrorBoundary: PageErrorBoundary };
                }),
              },
              {
                path: 'webhook-subscriptions',
                lazy: lazyWithRetry(async () => {
                  const { default: Component } = await import(
                    './pages/settings/webhook-subscriptions'
                  );
                  return { Component, ErrorBoundary: PageErrorBoundary };
                }),
              },
              {
                path: 'digital-channels',
                lazy: lazyWithRetry(async () => {
                  const { default: Component } = await import('./pages/settings/digital-channels');
                  return { Component, ErrorBoundary: PageErrorBoundary };
                }),
              },
              {
                path: 'sales-regions',
                lazy: lazyWithRetry(async () => {
                  const { default: Component } = await import('./pages/settings/sales-regions');
                  return { Component, ErrorBoundary: PageErrorBoundary };
                }),
              },
              {
                path: 'mailing-lists',
                lazy: lazyWithRetry(async () => {
                  const { default: Component } = await import('./pages/settings/mailing-lists');
                  return { Component, ErrorBoundary: PageErrorBoundary };
                }),
              },
              // Both lists now live on Mailing lists; old links and bookmarks land there.
              { path: 'fault-alerts', loader: () => redirect('/settings/mailing-lists') },
              { path: 'write-off-report', loader: () => redirect('/settings/mailing-lists') },
              {
                path: 'api-keys',
                lazy: lazyWithRetry(async () => {
                  const { default: Component } = await import('./pages/settings/api-keys');
                  return { Component, ErrorBoundary: PageErrorBoundary };
                }),
              },
              {
                path: 'api-consumers',
                lazy: lazyWithRetry(async () => {
                  const { default: Component } = await import('./pages/settings/api-consumers');
                  return { Component, ErrorBoundary: PageErrorBoundary };
                }),
              },
            ],
          },
        ],
      },
    ],
  },
]);
